/* @flow */

import { getDomain, getTopLevelDomain } from '../utils';
import { isSecure } from './browserutil';
import { isBoolean, isString } from './coreutils';
import { logDebug } from './logger';

/**
 * @module utils/cookie
 * @description Cookie utility functions
 * @requires utils
 */

const _isCookieSupported =
  window.navigator.cookieEnabled || Boolean(document.cookie.length);
export function isCookieSupported(): boolean {
  return _isCookieSupported;
}

/* Cookie handling helper functions */
/**
 * @description Helper to set cookie
 * @param {string} name - cookie key
 * @param {string|number} value - cookie value
 * @param {Document} doc - Document object
 * @param {number} expires - cookie expiry time in sec
 * @param {string} domain - domain for which cookie has to be set
 * @param {string} path - path in which cookie is set
 * @returns {undefined}
 */
export function setCookie(
  name: string = '',
  value: string | number = '',
  doc?: Document = window.document,
  expires?: number = 60 * 60 * 24 * 365,
  domain?: string = `.${getTopLevelDomain()}`, // Default to root domain with dot prefix for cross-subdomain support
  path?: string = '/',
  secure?: boolean = isSecure()
) {
  if (name.length === 0 || !isString(value)) {
    return;
  }
  const eDate = new Date();
  const expiresMS = expires * 1000; // Convert seconds to milliseconds
  eDate.setTime(eDate.getTime() + expiresMS);
  let cData = {
    [name]: encodeURIComponent(String(value)),
    expires: eDate.toUTCString(),
    SameSite: secure ? 'Strict' : '',
    secure
  };
  if (domain.length > 0) {
    cData = {
      ...cData,
      domain
    };
  }
  if (path.length > 0) {
    cData = {
      ...cData,
      path
    };
  }

  const newCookie = Object.entries(cData)
    .map(([k, v]) => {
      // $FlowFixMe[incompatible-type]
      if (isString(v)) {
        return [k, v].join('=');
      }
      if (isBoolean(v) && v === true) {
        return k;
      }
      return '';
    })
    .filter(v => v.length > 0) // remove empty values
    .join('; ');
  logDebug(`Setting cookie: ${name} = ${value} with new cookie: ${newCookie}`);
  // eslint-disable-next-line unicorn/no-document-cookie
  doc.cookie = newCookie;
}

/**
 * @description Helper to get cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {string} - cookie value
 */
export function getCookie(
  name: string = '',
  doc?: Document = window.document
): string {
  let ret = '';
  if (name.length > 0 && doc.cookie) {
    const m =
      // eslint-disable-next-line unicorn/prefer-string-raw
      doc.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]*)\\s*(;|$)`) || [];
    if (m.length >= 3) {
      ret = decodeURIComponent(m[2]);
    }
  }
  return ret;
}

/**
 * @description Helper to get all cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {Object} - Object of cookie name & value
 */
export function getAllCookies(
  doc?: Document = window.document
  // $FlowFixMe[unclear-type] unclear-type
): Object {
  const allCookies = doc.cookie || '';
  const cookieEntries = allCookies.split(';').map(v => {
    const [key, value] = v.split('=').map(a => a.trim());
    return [key, value];
  });
  return Object.fromEntries(cookieEntries);
}

export function getAllCookiesLength(doc?: Document = window.document): number {
  return (doc.cookie || '').length;
}

/**
 * @description Helper to delete cookie
 * @param {string} name - cookie key
 * @param {Document} doc - Document object
 * @returns {undefined}
 */
export function deleteCookie(
  name: string = '',
  doc?: Document = window.document
) {
  // clear cookie if set in TLD
  setCookie(name, '', doc, -1);
  if (getCookie(name).length === 0) {
    return;
  }
  // clear if it's set in current domain
  setCookie(name, '', doc, -1, getDomain());
  if (getCookie(name).length === 0) {
    return;
  }
  // clear if it's set without domain
  setCookie(name, '', doc, -1, '');
  if (getCookie(name).length === 0) {
    return;
  }
  // clear if it's set without domain & path
  setCookie(name, '', doc, -1, '', '');
}
